import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import logoLight from './logo-light.png';
import appScreenshot from './assets/app-screenshot.png';
import './App.css';

export default function Landing() {
  const navigate = useNavigate();
  const glowRef = useRef(null);
  const heroContentRef = useRef(null);
  const headlineRef = useRef(null);
  const phoneStageRef = useRef(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) navigate('/app', { replace: true });
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    function isLockedGlow() {
      if (typeof window === 'undefined') return false;
      return (
        window.matchMedia('(max-width: 968px)').matches ||
        window.matchMedia('(pointer: coarse)').matches
      );
    }

    function positionGlowBehindHeadline() {
      const h = headlineRef.current;
      const c = heroContentRef.current;
      if (!h || !c) return;
      const hr = h.getBoundingClientRect();
      const cr = c.getBoundingClientRect();
      if (hr.width === 0 && hr.height === 0) return;
      glow.style.left = `${hr.left - cr.left + hr.width / 2}px`;
      glow.style.top = `${hr.top - cr.top + hr.height / 2}px`;
    }

    let rafId = 0;
    function scheduleHeadlineGlow() {
      if (!isLockedGlow()) return;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        positionGlowBehindHeadline();
        rafId = 0;
      });
    }

    function onMouseMove(e) {
      if (isLockedGlow()) return;
      const c = heroContentRef.current;
      if (!c) return;
      const cr = c.getBoundingClientRect();
      glow.style.left = `${e.clientX - cr.left}px`;
      glow.style.top = `${e.clientY - cr.top}px`;
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', scheduleHeadlineGlow);

    const phone = phoneStageRef.current;
    let io = null;
    if (phone) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('active');
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(phone);
    }

    scheduleHeadlineGlow();
    const t = window.setTimeout(scheduleHeadlineGlow, 150);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', scheduleHeadlineGlow);
      cancelAnimationFrame(rafId);
      window.clearTimeout(t);
      if (io) io.disconnect();
    };
  }, []);

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    // iOS Safari uses navigator.standalone when launched from home screen
    const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone;
    setIsInstalled(Boolean(standalone || iosStandalone));
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isWebkit = /WebKit/.test(ua);
    const isCriOS = /CriOS/.test(ua);
    const isFxiOS = /FxiOS/.test(ua);
    setIsIosSafari(Boolean(isIos && isWebkit && !isCriOS && !isFxiOS));
  }, []);

  useEffect(() => {
    function onBeforeInstallPrompt(e) {
      // Prevent the mini-infobar and store the event for a user gesture.
      e.preventDefault();
      setInstallPrompt(e);
    }

    function onAppInstalled() {
      setIsInstalled(true);
      setInstallPrompt(null);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  async function onInstallClick(e) {
    e.preventDefault();
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } finally {
      // Can only be used once; reset after showing.
      setInstallPrompt(null);
    }
  }

  return (
    <div className="landing-page">
      <div className="landing-container">
        <nav className="landing-nav landing-nav-simple">
          <Link to="/app" className="landing-logo-link">
            <img src={logoLight} alt="LiftLab" className="landing-logo-img landing-logo-img-lg" />
          </Link>
          <Link to="/app" className="btn btn-landing-main btn-landing-main-sm">Sign In</Link>
        </nav>

        <section className="landing-hero landing-hero-pro">
          <div ref={heroContentRef} className="landing-hero-content landing-fade-in">
            <div ref={glowRef} className="landing-mouse-glow" aria-hidden />
            <div className="landing-hero-content-inner">
              <h1 ref={headlineRef} className="landing-hero-headline" aria-label="Train smarter. Lift heavier.">
                <span className="landing-hero-headline-line">Train</span>
                <span className="landing-hero-headline-line">Smarter.</span>
                <span className="landing-hero-headline-line">Lift heavier.</span>
              </h1>
              <p className="landing-hero-subtitle">
                Your intelligent training companion. Build custom programs, track progression with precision, and hit your goals with data-driven AI insights.
              </p>
              <div className="landing-btn-group">
                <Link to="/app" className="btn btn-landing-main">Get started for free</Link>
              </div>
              {!isInstalled && installPrompt && (
                <a href="#install" onClick={onInstallClick} className="landing-install-badge" aria-label="Install as web app">
                  <img
                    className="landing-badge-icon"
                    src={`${process.env.PUBLIC_URL}/logo192.png`}
                    alt=""
                    aria-hidden
                  />
                  <span className="landing-badge-labels">
                    <span className="landing-badge-sup">Available now</span>
                    <span className="landing-badge-title">Install as Web App</span>
                  </span>
                  <svg className="landing-badge-arrow" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </a>
              )}
            </div>
          </div>

          <div className="landing-hero-visual landing-hero-visual-pro">
            {!isInstalled && !installPrompt && isIosSafari && (
              <div className="landing-install-hint landing-install-hint-over-mockup">
                Safari: <strong>Share</strong> → <strong>Add to Home Screen</strong>
              </div>
            )}
            <div className="landing-phone-stage" ref={phoneStageRef}>
              <div className="landing-pop-card landing-pop-1 landing-floating">
                <div className="landing-pop-label">New Personal Best</div>
                <div className="landing-pop-val">Bench Press: 110 kg</div>
                <div className="landing-pop-bar-bg"><div className="landing-pop-bar" style={{ width: '85%' }} /></div>
              </div>

              <div className="landing-pop-card landing-pop-2 landing-floating-d1">
                <div className="landing-pop-label">Weekly Volume</div>
                <div className="landing-pop-val">+12.5% Progression</div>
                <div className="landing-mini-bars">
                  <div className="landing-mini-bar" style={{ height: '38%', background: '#2a2a2a' }} />
                  <div className="landing-mini-bar" style={{ height: '55%', background: '#333' }} />
                  <div className="landing-mini-bar" style={{ height: '72%', background: '#444' }} />
                  <div className="landing-mini-bar" style={{ height: '90%', background: 'var(--landing-primary)' }} />
                </div>
              </div>

              <div className="landing-phone-mockup landing-phone-mockup-shot">
                <div className="landing-notch" aria-hidden />
                <img className="landing-app-screen-img" src={appScreenshot} alt="LiftLab app screenshot" />
                <div className="landing-screen-glass" aria-hidden />
              </div>
            </div>

            {/* Install prompt moved under CTA */}
          </div>
        </section>
      </div>
    </div>
  );
}
